import React from "react"
import styled from "styled-components"
import { graphql } from "gatsby"

// Styling
import textStyles from "../styles/textStyles"
import mq from "../styles/breakpoints"

// Components
import Seo from "../components/Seo"
import ContentWrapper from "../components/ContentWrapper"
import PrivacySection from "../components/PrivacySection"

// Types
import { IPrivacyPage } from "../prismic-types/privacy-page"

interface IProps {
  content: IPrivacyPage
}

const PrivacyPage: React.FC<IProps> = ({ content }) => {
  const slug = "privacy"
  return (
    <>
      <Seo
        title={content.meta.title}
        description={content.meta.description}
        dateModified={content.meta.modifiedDate}
        datePublished={content.meta.publishedDate}
        lang={content.meta.lang}
        slug={slug}
        breadCrumbItems={[{ name: "Privacy", url: slug }]}
      />
      <Container>
        <StyledContentWrapper>
          <Title>{content.page.title}</Title>
          <Sections>
            {content.sections.map((section, i) => (
              <StyledPrivacySection
                key={section.title}
                nr={i + 1}
                title={section.title}
                text={section.text}
              />
            ))}
          </Sections>
        </StyledContentWrapper>
      </Container>
    </>
  )
}

const Container = styled.section`
  margin-bottom: 120px;
  padding-top: 100px;

  ${mq.from.S`
    margin-bottom: 240px;
    padding-top: 200px;
  `}
`

const StyledContentWrapper = styled(ContentWrapper)`
  ${mq.from.M`
    max-width: calc(10/12*100%);
  `}

  ${mq.from.L`
    max-width: calc(8/12*100%);
  `}
`

const Title = styled.h1`
  ${textStyles.titleXL};
  z-index: 1;
  position: relative;
  margin-bottom: 40px;

  ${mq.from.S`
    margin-bottom: 64px;
  `}
`

const Sections = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const StyledPrivacySection = styled(PrivacySection)`
  margin-bottom: 40px;

  ${mq.from.S`
    margin-bottom: 64px;
  `}

  :last-of-type {
    margin-bottom: 0;
  }
`

export const query = graphql`
  query privacyPage($locale: String!) {
    prismicPrivacyPage(lang: { eq: $locale }) {
      last_publication_date
      lang
      first_publication_date
      data {
        # Meta
        meta_title
        meta_description

        # Page
        title {
          text
        }

        # Sections
        sections {
          section_title {
            text
          }
          section_text {
            html
          }
        }
      }
    }
  }
`

export default ({ data }: any) => {
  const {
    data: raw,
    first_publication_date,
    last_publication_date,
    lang,
  } = data.prismicPrivacyPage

  const meta = {
    title: raw.meta_title,
    description: raw.meta_description,
    publishedDate: first_publication_date,
    modifiedDate: last_publication_date,
    lang,
  }

  const page = {
    title: raw.title.text,
  }

  const sections = raw.sections.map((section: any) => {
    return {
      title: section.section_title.text,
      text: section.section_text.html,
    }
  })

  const content = {
    meta,
    page,
    sections,
  }

  return <PrivacyPage content={content} />
}
